import { hmac } from "@noble/hashes/hmac";
import { sha256 } from "@noble/hashes/sha2";
import { bytesToHex, hexToBytes, utf8ToBytes, randomBytes } from "@noble/hashes/utils";
/**
 * Advertised round length. A bust past this ceiling is clamped to stay visible
 * on screen — the math independently targets 1% house edge via the Stake-style
 * crash CDF regardless of the ceiling.
 */
export const ROUND_MAX_MS = 30_000;
/**
 * Multiplier rise curve: mult(t) = RISE_BASE ^ (RISE_RATE_PER_SEC * t_seconds).
 * Matches the trading-terminal "confident climb" vibe the brand spec calls for.
 */
export const RISE_BASE = 1.06;
export const RISE_RATE_PER_SEC = 3;
export function commitSeed() {
    const seedBytes = randomBytes(32);
    const serverSeed = bytesToHex(seedBytes);
    const serverSeedHash = bytesToHex(sha256(seedBytes));
    return { serverSeed, serverSeedHash };
}
export function hashServerSeed(serverSeedHex) {
    return bytesToHex(sha256(hexToBytes(serverSeedHex)));
}
function hmacHex(keyHex, message) {
    return bytesToHex(hmac(sha256, hexToBytes(keyHex), utf8ToBytes(message)));
}
/**
 * Primedice-style inverse-uniform crash CDF. Exact 1.0% house edge:
 *
 *   crash = max(1.00, floor(99 · E / (h+1)) / 100)     where E = 2^52, h ~ U[0, E)
 *
 * Under cashout-at-c, P(crash ≥ c) ≈ 99 / (100·c), so expected return is
 *   c · P(crash ≥ c) = 0.99  — invariant across all flat cashout strategies.
 * The floor-to-1.00 region naturally produces ~2% instabusts.
 */
function hmacToCrashMultiplier(hmacHexStr) {
    const first13 = hmacHexStr.slice(0, 13);
    const h = BigInt("0x" + first13);
    const e = 1n << 52n;
    const crash = Number((99n * e) / (h + 1n)) / 100;
    return Math.max(1.0, crash);
}
export function timeForMultiplier(m) {
    if (m <= 1)
        return 0;
    return (Math.log(m) / (Math.log(RISE_BASE) * RISE_RATE_PER_SEC)) * 1000;
}
export function multiplierAt(tMs) {
    const seconds = Math.max(0, tMs) / 1000;
    return Math.max(1, Math.pow(RISE_BASE, RISE_RATE_PER_SEC * seconds));
}
export function rollRound(input) {
    const { serverSeed, clientSeed, nonce } = input;
    const bustHmac = hmacHex(serverSeed, `${clientSeed}:${nonce}:bust`);
    const bustMultiplier = hmacToCrashMultiplier(bustHmac);
    const bustTimeMs = Math.round(Math.min(ROUND_MAX_MS, timeForMultiplier(bustMultiplier)));
    const crackTimesMs = distributeCrackTimes(serverSeed, clientSeed, nonce, bustTimeMs);
    return { bustMultiplier, bustTimeMs, crackTimesMs };
}
function distributeCrackTimes(serverSeed, clientSeed, nonce, bustTimeMs) {
    // On instabust (bustTimeMs ~ 0) all five locks blow at t=0 simultaneously.
    if (bustTimeMs < 5)
        return [0, 0, 0, 0, bustTimeMs];
    const crackHmac = hmacHex(serverSeed, `${clientSeed}:${nonce}:cracks`);
    const E = Number(1n << 52n);
    const floats = [];
    for (let i = 0; i < 4; i++) {
        const chunk = crackHmac.slice(i * 13, i * 13 + 13);
        const val = Number(BigInt("0x" + chunk));
        floats.push(val / E);
    }
    floats.sort((a, b) => a - b);
    const ceiling = Math.max(0, bustTimeMs - 1);
    const minGap = Math.max(1, Math.floor(bustTimeMs / 120));
    const interior = floats.map((f) => Math.min(ceiling, Math.max(0, Math.round(f * bustTimeMs * 0.92))));
    for (let i = 1; i < interior.length; i++) {
        const prev = interior[i - 1] ?? 0;
        const curr = interior[i] ?? 0;
        if (curr <= prev)
            interior[i] = Math.min(ceiling, prev + minGap);
    }
    // ensure the last interior crack stays strictly before bust
    const last = interior[3] ?? 0;
    if (last >= bustTimeMs)
        interior[3] = Math.max(0, bustTimeMs - 1);
    return [
        interior[0] ?? 0,
        interior[1] ?? 0,
        interior[2] ?? 0,
        interior[3] ?? 0,
        bustTimeMs,
    ];
}
export function verifyRound(input) {
    const hashMatch = hashServerSeed(input.serverSeed) === input.claimedServerSeedHash;
    const rolled = rollRound(input);
    const multiplierMatch = rolled.bustMultiplier === input.claimedBustMultiplier;
    return { hashMatch, multiplierMatch, valid: hashMatch && multiplierMatch, rolled };
}
//# sourceMappingURL=provably-fair.js.map